'use client';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';

import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { isSellable } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Cart + favourite buttons of the product card (52 px, gap 8). Both are the core buttons – cart
 * flow, variant dialog, ask-for-price and favourites logic stay in core; only the look is DLZ.
 *   labelled  „Do košíka“ with the cart icon (Akciové ponuky), otherwise the round icon button
 *   compact   below `md` the 40 px round cart icon button only, no favourites (Figma „Mobile / Product card“)
 *   overlay   below `md` the 40 px cart button and the favourite as a 32 px white circle over the card's
 *             image (Figma „Mobile / Product card“ of the 2-column grids, 60459:98049) – placed against
 *             the card, which is a size container: the image is as wide as the card's content box
 */
export function ProductActions({
  product,
  labelled,
  compact = false,
  overlay = false,
  showCart,
  showFavorite,
}: {
  product: Product;
  labelled: boolean;
  compact?: boolean;
  overlay?: boolean;
  showCart: boolean;
  showFavorite: boolean;
}) {
  if (!showCart && !showFavorite) return null;

  return (
    <div className={cn('relative flex shrink-0 items-center gap-2', overlay && 'max-md:static')}>
      {showCart ? (
        <AddToCartButton
          product={product}
          quantity={1}
          sellable={isSellable(product, 1)}
          customText="text1115"
          iconLeft="cart"
          size={labelled ? 'l' : 'icon'}
          // The core button renders its caption twice (container-query variants); the icon
          // button keeps it as the accessible name only.
          className={cn(
            'space-x-0',
            !labelled && '[&>span]:sr-only',
            (compact || overlay) && 'max-md:size-10 max-md:p-0 max-md:[&>span]:sr-only',
          )}
        />
      ) : null}
      {showFavorite ? (
        // The core button styles its own root, so the whole control is hidden from the wrapper.
        <div
          className={cn(
            overlay
              ? // 12 px card padding + the square image – 8 px inset – the 32 px button.
                'z-10 max-md:absolute max-md:right-5 max-md:top-[calc(100cqw-28px)] md:contents'
              : 'contents',
            compact && 'max-md:hidden',
          )}
        >
          <AddToFavoritesButton
            slug={product.slug}
            productId={product.id}
            isFavorite={product.is_favorite ?? false}
            buttonVariant="outline"
            buttonSize="icon"
            buttonClassName={cn(
              overlay &&
                'max-md:size-8 max-md:border-0 max-md:p-0 max-md:shadow-[0_0_12.8px_rgba(15,16,18,0.1)]',
            )}
            className={cn('size-6 text-current hover:text-current', overlay && 'max-md:size-5')}
          />
        </div>
      ) : null}
    </div>
  );
}
